/**
 * Draw flow opportunity markers on the chart pane at (time × strike).
 * Green ▲ = buy call / sell put; red ▼ = sell call / buy put.
 */
import type {
    IPrimitivePaneRenderer,
    IPrimitivePaneView,
    ISeriesPrimitive,
    SeriesAttachedParameter,
    Time,
} from "lightweight-charts";
import type { FlowOpportunityMarker } from "../../utils/flowVolumeProfile";

const BULL_FILL = "rgba(34, 197, 94, 0.7)";
const BULL_STROKE = "rgba(52, 211, 153, 0.95)";
const BEAR_FILL = "rgba(239, 68, 68, 0.7)";
const BEAR_STROKE = "rgba(248, 113, 113, 0.95)";
const WHALE_RING = "#facc15";

function markerRadius(premium: number): number {
    return 4 + Math.min(14, Math.sqrt(premium / 6_000) * 4);
}

function drawBull(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    r: number,
) {
    ctx.beginPath();
    ctx.moveTo(x, y - r);
    ctx.lineTo(x + r * 0.9, y + r * 0.8);
    ctx.lineTo(x - r * 0.9, y + r * 0.8);
    ctx.closePath();
}

function drawBear(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    r: number,
) {
    ctx.beginPath();
    ctx.moveTo(x, y + r);
    ctx.lineTo(x + r * 0.9, y - r * 0.8);
    ctx.lineTo(x - r * 0.9, y - r * 0.8);
    ctx.closePath();
}

class FlowMarkersPaneRenderer implements IPrimitivePaneRenderer {
    constructor(private primitive: FlowOpportunityMarkersPrimitive) {}

    draw(target: {
        useMediaCoordinateSpace: (
            fn: (scope: {
                context: CanvasRenderingContext2D;
                mediaSize: { width: number; height: number };
            }) => void,
        ) => void;
    }): void {
        const param = this.primitive.params;
        const markers = this.primitive.markers;
        if (!param || !markers.length) return;

        target.useMediaCoordinateSpace((scope) => {
            const { chart, series } = param;
            const ctx = scope.context;
            const ts = chart.timeScale();
            const plotW = ts.width();

            for (const op of markers) {
                const sec = Math.floor(op.timestamp / 1000) as Time;
                const x = ts.timeToCoordinate(sec);
                const y = series.priceToCoordinate(op.chartPrice);
                if (x == null || y == null) continue;
                if (x < -12 || x > plotW + 12) continue;
                if (y < -12 || y > scope.mediaSize.height + 12) continue;

                const r = markerRadius(op.premium);
                const bull = op.direction === "bullish";

                if (bull) {
                    drawBull(ctx, x, y, r);
                    ctx.fillStyle = BULL_FILL;
                    ctx.fill();
                    ctx.strokeStyle = BULL_STROKE;
                } else {
                    drawBear(ctx, x, y, r);
                    ctx.fillStyle = BEAR_FILL;
                    ctx.fill();
                    ctx.strokeStyle = BEAR_STROKE;
                }
                ctx.lineWidth = op.isWhale ? 2.25 : 1.5;
                ctx.stroke();

                if (op.isWhale) {
                    ctx.beginPath();
                    ctx.arc(x, y, r + 3.5, 0, Math.PI * 2);
                    ctx.strokeStyle = WHALE_RING;
                    ctx.lineWidth = 2;
                    ctx.stroke();
                }
            }
        });
    }
}

class FlowMarkersPaneView implements IPrimitivePaneView {
    private rendererInst: FlowMarkersPaneRenderer;

    constructor(primitive: FlowOpportunityMarkersPrimitive) {
        this.rendererInst = new FlowMarkersPaneRenderer(primitive);
    }

    zOrder(): "top" {
        return "top";
    }

    renderer(): IPrimitivePaneRenderer {
        return this.rendererInst;
    }
}

export class FlowOpportunityMarkersPrimitive implements ISeriesPrimitive<Time> {
    params: SeriesAttachedParameter<Time, "Candlestick"> | null = null;
    markers: FlowOpportunityMarker[] = [];
    private paneView: FlowMarkersPaneView;

    constructor() {
        this.paneView = new FlowMarkersPaneView(this);
    }

    attached(param: SeriesAttachedParameter<Time, "Candlestick">): void {
        this.params = param;
    }

    detached(): void {
        this.params = null;
    }

    paneViews(): IPrimitivePaneView[] {
        return [this.paneView];
    }

    updateAllViews(): void {
        /* markers read on each draw */
    }

    setMarkers(markers: FlowOpportunityMarker[]): void {
        this.markers = markers;
        this.params?.requestUpdate();
    }
}
